<template>
<div v-if='cardData.kind && cardData.kind === `room_lunch`'
     class='b-plan_table__label__wrapper--disabled'>
    <div class='h-font-12 h-fw-500 h-opacity-0_5 h-flex h-flex-center'>
        <span class='h-mr-5'>
            {{ cardData.title }}
        </span>
    </div>
</div>
<div v-else-if='cardData.title === `reserved_slot_drive`'
     :style='styles'
     class='b-reserved-slot-card'>
    <div class='b-reserved-slot-card__title'>
        {{ $t('CALENDAR.POPUP.RESERVED.SLOT.DRIVE.BACK.TITLE') }}
    </div>
</div>
<div
    v-else-if='cardData.origin === `from_reserved_slot`'
    ref='reservedSlotCard'
    :style='styles'
    class='b-reserved-slot-card'
    :class='{
        "b-reserved-slot-card--compact": eventHeight < 45,
        "b-reserved-slot-card--title-only": eventHeight < 28,
        "b-reserved-slot-card--info-open": isReservedSlotInfoOpen
    }'>
    <span
        v-if='shouldShowReservedSlotInfo'
        class='b-reserved-slot-card__info'
        :aria-label='$t("CALENDAR.POPUP.RESERVED.SLOT.TITLE")'
        tabindex='0'
        @mouseenter='isReservedSlotInfoOpen = true'
        @mouseleave='isReservedSlotInfoOpen = false'
        @focus='isReservedSlotInfoOpen = true'
        @blur='isReservedSlotInfoOpen = false'>
        <FwIcon
            class='b-reserved-slot-card__info--icon'
            icon='alert-info'
            size='12'>
        </FwIcon>

        <span
            v-if='isReservedSlotInfoOpen'
            class='b-reserved-slot-card__tooltip'>
            <strong class='b-reserved-slot-card__tooltip-title'>
                {{ $t('CALENDAR.POPUP.RESERVED.SLOT.TITLE') }}
            </strong>

            <span
                v-for='method in methods'
                :key='method'
                class='b-reserved-slot-card__tooltip-line'>
                {{ $t(methodsMapping[method].text) }}
            </span>

            <span
                v-if='cardData.description'
                class='b-reserved-slot-card__tooltip-line'>
                {{ cardData.description }}
            </span>
        </span>
    </span>

    <div
        ref='reservedSlotContent'
        class='b-reserved-slot-card__content'>
        <div class='b-reserved-slot-card__title'>
            {{ $t('CALENDAR.POPUP.RESERVED.SLOT.TITLE') }}
        </div>

        <div
            v-for='method in methods'
            :key='method'
            class='b-reserved-slot-card__method'>
            <FwIcon
                :icon='methodsMapping[method].icon'
                size='10'
                class='b-reserved-slot-card__icon'>
            </FwIcon>

            <span class='b-reserved-slot-card__text'>
                {{ $t(methodsMapping[method].text) }}
            </span>
        </div>

        <div
            v-if='cardData.description && showAppointmentType'
            class='b-reserved-slot-card__type'>
            {{ cardData.description }}
        </div>
    </div>
</div>
<drag v-else
      class='drag'
      tag='div'
      :style='styles'
      :transfer-data='{ card: cardData }'
      @dragstart='data => $emit("dragstart", data)'
      @dragend='data => $emit("dragend", data)'
      @drag='data => $emit("drag", data)'>
    <div class='b-plan_table__label__wrapper'
         :class='{
             "b-plan_table__label__wrapper--drive": isDriveEvent,
             "b-plan_table__label__wrapper--small": eventHeight <= 15
         }'>
        <div class='b-plan_table__label'
             :class='{"b-plan_table__transparent": isUnavailabilityEvent }'>
            <div v-if='time && !isTimeHidden'
                 class='b-plan_table__label--header qa-plan-table-card__time'>
                {{ time }}
            </div>
            <div class='h-bold h-overflow-hidden'>
                <p v-if='isUnavailabilityEvent'
                   class='h-m-0 b-plan_table__label--small qa-plan-table-card__unavailability-title'
                   :class='{"b-plan_table__label__overflow-text": eventHeight <= 70}'>
                    {{ $t(unavailabilityTitle) }}
                </p>
                <p v-else-if='isDriveEvent'
                   class='h-m-0 qa-plan-table-card__unavailability-title'
                   :class='{"b-plan_table__label__overflow-text": eventHeight <= 70}'>
                    🚗 {{ $t('EVENT.DRIVE.TITLE') }}
                </p>
                <p v-else
                   class='h-m-0 qa-plan-table-card__title'
                   :class='{"b-plan_table__label__overflow-text": eventHeight <= 70}'>
                    {{ privateEvent ? $t('EVENT.PRIVATE.TITLE') : cardData.title }}
                </p>
            </div>
            <div :style='{ background: color }'
                 class='b-plan_table__label__before'></div>
            <div v-if='cardData.origin === `from_appointment` && cardData.booking_platform !== undefined'
                 class='b-plan_table__label__appointment'
                 :class='accountBookedTypeClass'>
            </div>
        </div>
    </div>
</drag>
</template>

<script lang='ts'>
import { Component, mixins, Prop } from 'vue-facing-decorator';
import dayjs from 'dayjs';
import { Drag } from 'vue3-drag-drop';
import { TranslateResult } from 'vue-i18n';
import { CalendarInstanceType } from '@/types/Events/CalendarInstanceType';
import DateMixin from '@/mixins/dateMixin';
import { EVENT_ORIGIN_DRIVE } from '@/helpers/events';
import { unavailabilityTitleTranslate } from '@/helpers/translate';
import { EventDataType } from '@/types/Events';
import { METHODS_SLOTS_MAPPING } from '@/helpers/appointment';

@Component({
    emits: ['dragstart', 'dragend', 'drag'],
    components: { Drag },
})
export default class PlanTableCard extends mixins(DateMixin) {
    @Prop({ type: Object, required: true })
    readonly cardData!: CalendarInstanceType;

    @Prop({ type: Object, default: null })
    readonly timeBlock!: EventDataType;

    isTimeHidden: boolean = false;
    showAppointmentType: boolean = true;
    isReservedSlotInfoOpen: boolean = false;
    reservedSlotResizeObserver: ResizeObserver | null = null;

    async mounted(): Promise<void> {
        if (this.cardData.origin !== 'from_reserved_slot') {
            return;
        }

        await this.$nextTick();

        const card = this.$refs.reservedSlotCard as HTMLElement | undefined;

        if (!card) {
            return;
        }

        this.reservedSlotResizeObserver = new ResizeObserver(() => {
            // eslint-disable-next-line no-void
            void this.updateAppointmentTypeVisibility();
        });

        this.reservedSlotResizeObserver.observe(card);

        await this.updateAppointmentTypeVisibility();
    }

    beforeUnmount(): void {
        this.reservedSlotResizeObserver?.disconnect();
        this.reservedSlotResizeObserver = null;
    }

    get styles() {
        const eventHour = dayjs(this.cardData.dt_start).hour();
        const firstHour = this.timeBlock ?
            this.timeBlock.hours :
            eventHour;

        return {
            height: this.cardData.full_day ?
                '38px' :
                `${this.eventHeight}px`,
            transform: `translate(
                0,
                ${(eventHour - firstHour) * 60 +
            dayjs(this.cardData.dt_start).minute()}px
            )`,
        };
    }

    get eventHeight(): number {
        return dayjs(this.cardData.dt_end).diff(dayjs(this.cardData.dt_start), 'minutes');
    }

    get accountBookedTypeClass(): string | null {
        if (this.cardData.booking_platform === undefined) {
            return null;
        }
        if (this.cardData.booking_platform === 'sc') {
            return 'b-plan_table__label__appointment--sc';
        }
        return 'b-plan_table__label__appointment--sass';
    }

    get isUnavailabilityEvent(): boolean {
        return this.cardData.origin === 'from_unavailability';
    }

    get isDriveEvent(): boolean {
        return this.cardData.origin === EVENT_ORIGIN_DRIVE;
    }

    get methods(): string[] {
        return (this.cardData?.locations ?? []).filter(
            location => Boolean(this.methodsMapping[location]),
        );
    }

    get methodsMapping() {
        return METHODS_SLOTS_MAPPING;
    }

    get privateEvent() {
        return this.cardData.is_private && this.cardData.user_id !== this.user.id;
    }

    get unavailabilityTitle() {
        return unavailabilityTitleTranslate(this.cardData.title);
    }

    get time(): null | string | TranslateResult {
        if (this.cardData.start_from_morning || this.cardData.fully_day) {
            return null;
        }
        if (this.cardData.full_day) {
            return this.$i18n.t('CALENDAR.FULL.DAY');
        }
        return this.getCurrentDateAsMinutesAMPM(new Date(this.cardData.dt_start_view || this.cardData.dt_start), false);
    }

    get color() {
        const colorsData: Array<string> = this.cardData.color.split(' - ');
        return `linear-gradient(${colorsData[0]}, ${colorsData[1]})`;
    }

    get shouldShowReservedSlotInfo(): boolean {
        const hasAppointmentType = Boolean(this.cardData.description);
        const hasMethods = this.methods.length > 0;

        if (this.eventHeight < 28) {
            return hasMethods || hasAppointmentType;
        }

        if (this.eventHeight < 45) {
            return hasAppointmentType;
        }

        return hasAppointmentType && !this.showAppointmentType;
    }

    async updateAppointmentTypeVisibility(): Promise<void> {
        if (
            this.cardData.origin !== 'from_reserved_slot' ||
            !this.cardData.description ||
            this.eventHeight < 45
        ) {
            this.showAppointmentType = false;
            return;
        }

        this.showAppointmentType = true;

        await this.$nextTick();

        const card = this.$refs.reservedSlotCard as HTMLElement | undefined;
        const content = this.$refs.reservedSlotContent as HTMLElement | undefined;

        if (!card || !content) {
            return;
        }

        this.showAppointmentType = content.scrollHeight <= card.clientHeight;
    }
}
</script>

<style lang='sass'>
.b-plan_table__label__wrapper--disabled
    display: flex
    justify-content: center
    align-items: center
    pointer-events: none
    height: 100%
    background: rgba(255, 255, 255, 0.6)
    color: $dark-blue
    font-size: 12px
    padding: 4px 7px
    position: relative
    border-radius: 5px
    overflow: hidden

.b-plan_table__label
    height: 100%
    background: linear-gradient(to bottom, #f9f9f9 0%, #ffffff 12%, #ffffff 87%, #f9f9f9 100%)
    color: $dark-blue
    font-size: 12px
    padding: 4px 7px 4px 12px
    position: relative
    border-radius: 5px
    box-shadow: 0 5px 11px 0 rgba(33, 63, 107, 0.1)
    overflow: hidden

    &--header
        font-weight: 300

    &--header, &__overflow-text
        width: 75px
        text-overflow: ellipsis
        overflow: hidden
        white-space: nowrap

    &--small
        text-transform: lowercase

    &__wrapper
        padding: 0 4px
        height: 100%

        &--small
            .b-plan_table__label
            padding: 0 7px 0 12px

    &__before
        left: 0
        width: 5px
        height: 100%
        position: absolute
        top: 0
        border-radius: 5px 0 0 5px

    &__appointment
        right: 0
        width: 8px
        height: 8px
        position: absolute
        bottom: 0
        border-radius: 5px 0 5px 0

        &--sc
            background: #37B4FB !important

        &--saas
            background: #FB7C07 !important

.b-plan_table__transparent
    background: #EEF0F4

.b-reserved-slot-card
    width: 100% !important
    box-sizing: border-box
    pointer-events: none
    position: absolute !important
    z-index: 1 !important
    overflow: hidden
    background-color: #f0f2f5
    border-radius: 5px
    color: #8491a3

    &:hover
        background-color: #f0f2f5 !important

    &--info-open
        z-index: 20 !important
        overflow: visible

    &__content
        display: flex
        width: 100%
        min-width: 0
        height: 100%
        box-sizing: border-box
        flex-direction: column
        align-items: flex-start
        gap: 3px
        padding: 8px 4px
        overflow: hidden

    &__title
        width: 100%
        flex-shrink: 0
        color: #738197
        font-size: 12px
        font-weight: 600
        line-height: 14px
        text-transform: uppercase
        white-space: nowrap

    &__method
        display: flex
        width: 100%
        min-width: 0
        flex-shrink: 0
        align-items: flex-start
        gap: 3px
        font-size: 10px
        font-weight: 500
        line-height: 13px

    &__icon
        flex-shrink: 0
        margin-top: 1px

    &__text
        min-width: 0
        white-space: normal
        overflow-wrap: break-word

    &__type
        width: 100%
        min-width: 0
        flex-shrink: 0
        margin-top: 2px
        font-size: 10px
        font-weight: 600
        line-height: 13px
        white-space: normal
        overflow-wrap: break-word

    &__info
        position: absolute
        padding: 10px
        z-index: 21
        top: 0
        right: 0
        display: flex
        align-items: center
        justify-content: center
        width: 16px
        height: 16px
        border-radius: 50%
        color: #738197
        cursor: help
        pointer-events: auto
        &--icon
            line-height: 10px

        &:hover,
        &:focus
            background: rgba(115, 129, 151, 0.14)
            outline: none

    &__tooltip
        position: absolute
        z-index: 22
        top: 20px
        right: 0
        display: flex
        width: max-content
        max-width: 260px
        box-sizing: border-box
        flex-direction: column
        gap: 4px
        padding: 8px
        border-radius: 4px
        background: #ffffff
        box-shadow: 0 4px 12px rgba(33, 63, 107, 0.2)
        color: #526176
        cursor: default
        font-size: 11px
        line-height: 14px
        white-space: normal

    &__tooltip-title
        color: #738197
        font-size: 11px
        text-transform: uppercase

    &__tooltip-line
        overflow-wrap: break-word

.b-reserved-slot-card--compact
    .b-reserved-slot-card__content
        gap: 1px
        padding: 2px 4px

    .b-reserved-slot-card__title
        line-height: 12px

    .b-reserved-slot-card__method
        line-height: 10px

    .b-reserved-slot-card__icon
        margin-top: 0

    .b-reserved-slot-card__type
        display: none

.b-reserved-slot-card--title-only
    .b-reserved-slot-card__content
        padding: 1px 4px

    .b-reserved-slot-card__method,
    .b-reserved-slot-card__type
        display: none
</style>
